import { useQuery } from '@tanstack/react-query';
import { api, fetchAllPages, qs } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * The reference data every view needs to turn ids into names: drivers,
 * quarries, sites, materials. All four change rarely (a fleet gains a
 * driver a few times a year), so they carry a long staleTime and are shared
 * by cache key across the orders table, the dispatch modal, the map and the
 * fleet grid — where the monolith refetched drivers on every single orders
 * table render.
 */

export function useDrivers({ includeInactive = false } = {}) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.drivers(tenantId, { includeInactive }),
    queryFn: () =>
      api(`/api/tenants/${tenantId}/drivers${includeInactive ? '?include_inactive=1' : ''}`),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    placeholderData: [],
  });
}

/**
 * One page of quarries. `{data, total_count}` from the endpoint, unwrapped:
 * `data` is the page, `totalCount` is the size of the whole filtered set.
 *
 * If you need EVERY quarry — a map to draw, a picker to fill — use
 * useAllQuarries() below and say so. This hook deliberately does not treat
 * "no paging arguments" as "give me everything": that convention reads
 * innocently at the call site and quietly fetches the entire table.
 */
export function useQuarries({ search, category, limit, offset, includeInactive = false } = {}) {
  return useQuarryQuery({ search, category, limit, offset, includeInactive, all: false });
}

/**
 * Every quarry, paged through to the end.
 *
 * The map, the order form and the approval modal all read this. Before it
 * existed they called useQuarries() and got whatever one page held — which
 * was the whole table until this endpoint paginated, and silently the first
 * page of it afterwards.
 */
export function useAllQuarries({ search, category, includeInactive = false } = {}) {
  return useQuarryQuery({ search, category, includeInactive, all: true });
}

function useQuarryQuery({ search, category, limit, offset, includeInactive, all }) {
  const tenantId = useTenantId();
  const path = `/api/tenants/${tenantId}/quarries`;
  // Archived quarries stay out unless asked for — the map, the order form
  // and the approval modal all read this hook, and none of them should be
  // offered a quarry that has been archived.
  const filters = { search, category, include_inactive: includeInactive ? '1' : undefined };

  const query = useQuery({
    queryKey: qk.quarries(tenantId, { search, category, limit, offset, includeInactive, all }),
    queryFn: () =>
      all
        ? fetchAllPages(path, filters)
        : api(`${path}${qs({ ...filters, limit, offset })}`),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
    placeholderData: { data: [], total_count: 0 },
  });

  return { ...query, data: query.data?.data ?? [], totalCount: query.data?.total_count ?? 0 };
}

/** Same archived-rows opt-in as useQuarries above, and off by default for
 *  the same reason: the order form must not offer a delivery site that has
 *  been archived. */
export function useSites({ includeInactive = false } = {}) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.sites(tenantId, { includeInactive }),
    queryFn: () =>
      api(`/api/tenants/${tenantId}/sites${includeInactive ? '?include_inactive=1' : ''}`),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
    placeholderData: [],
  });
}

export function useMaterials({ includeInactive = false } = {}) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.materials(tenantId, { includeInactive }),
    queryFn: () =>
      api(`/api/tenants/${tenantId}/materials${includeInactive ? '?include_inactive=1' : ''}`),
    enabled: Boolean(tenantId),
    staleTime: 5 * 60_000,
    placeholderData: [],
  });
}

/** driver_id -> driver row, for the id->name lookups the tables do per row. */
export function useDriverMap() {
  const { data: drivers = [] } = useDrivers({ includeInactive: true });
  return Object.fromEntries(drivers.map((d) => [d.driver_id, d]));
}
